<template>
  <ul class="sharing">
    <li
      v-for="(method, index) in shareMethods"
      :key="index"
      class="sharing-item sharing__item"
      :class="`sharing-item--${method.class || method.name}`"
      @click="share(method)"
    >
      <SvgAsset
        class="sharing-item__icon"
        :content="method.icon"
      />
      <span class="sharing-item__title">{{ method.title }}</span>
    </li>
  </ul>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import Sharer from 'sharer.npm.js';
  import type { IFullArticle, TArticle, TImageSize } from '~/types/common';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import { useSeoStore } from '~/store/seo';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import checkPath from '@/assets/images/svg/check.svg?raw';
  import linkPath from '@/assets/images/svg/link.svg?raw';
  import vkSharePath from '@/assets/images/svg/vk-share.svg?raw';
  import twitterPath from '@/assets/images/svg/twitter.svg?raw';
  import telegramPath from '@/assets/images/svg/telegram.svg?raw';
  import viberPath from '@/assets/images/svg/viber.svg?raw';
  import pinterestPath from '@/assets/images/svg/pinterest.svg?raw';

  export default defineNuxtComponent({
    name: 'SharingList',
    components: {
      SvgAsset,
    },

    inject: {
      // Контекст события для аналитики
      // Где находится элемент с которым происходит событие
      eventItemSharingList: {
        default() {
          return () => '';
        },
      },
    },

    props: {
      article: {
        type: Object as PropType<TArticle | IFullArticle>,
        required: true,
      },

      shown: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: null,
      },
    },

    data() {
      return {
        copyFinished: false as boolean,
      };
    },

    computed: {
      shareMethods(): [] {
        return [
          {
            title: this.copyFinished ? 'Скопировано' : 'Скопировать ссылку',
            name: 'copy',
            class: this.copyFinished ? 'finished' : undefined,
            icon: this.copyFinished ? checkPath : linkPath,
          },
          {
            shareUrl: 'http://vk.com/share.php',
            params: {
              url: this.article.url,
            },

            title: 'Vkontakte',
            name: 'vkontakte',
            icon: vkSharePath,
          },

          {
            shareUrl: 'https://twitter.com/intent/tweet',
            params: {
              text: useSeoStore().currentSeo.title,
              url: this.article.url,
              via: 'BurningHutRu',
            },

            title: 'Twitter',
            name: 'twitter',
            icon: twitterPath,
          },
          {
            shareUrl: 'https://t.me/share/url',
            params: {
              text: `${this.article.title}`,
              url: this.article.url,
            },

            title: 'Telegram',
            name: 'telegram',
            icon: telegramPath,
          },
          {
            shareUrl: 'viber://forward',
            params: {
              text: `${this.article.title} \n${this.article.url}`,
              url: this.article.url,
            },

            title: 'Viber',
            name: 'viber',
            icon: viberPath,
          },
          {
            shareUrl: 'https://www.pinterest.com/pin/create/button/',
            params: {
              url: this.article.url,
              media: this.image?.url,
            },

            title: 'Pinterest',
            name: 'pinterest',
            icon: pinterestPath,
          },
        ];
      },

      image(): TImageSize | undefined {
        return this.article?.image.sizes?.find((item) => item.width === 746);
      },

      currentslug(): string {
        return getSlugFromUrl(useRoute().fullPath);
      },
    },

    watch: {
      shown() {
        this.copyFinished &&
          setTimeout(() => {
            this.copyFinished = false;
          }, 400);
      },
    },

    methods: {
      share(shareMethodParams: any): void {
        this.sendAnalytics(shareMethodParams);

        if (shareMethodParams.name === 'copy') {
          navigator.clipboard.writeText(this.article.url).then(() => {
            this.copyFinished = true;
          });
          return;
        }

        const sharer = new Sharer(null);
        sharer.urlSharer({
          height: 600,
          width: 600,
          ...shareMethodParams,
        });
      },

      // Функция для отправки аналитики клика социальной активности
      sendAnalytics(shareMethodParams: any) {
        this.$sendYandexMetrika({
          level1: 'Социальная активность',
          level5: shareMethodParams.title,
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use 'sass:map';

  @mixin sharingItemTheme($params) {
    $hoverBg: map.get($params, 'hoverBg');
    $hoverIcon: map.get($params, 'hoverIcon');
    $this: '.sharing-item';

    @include hover {
      :deep() {
        svg path {
          fill: $hoverBg;
        }
      }

      #{$this} {
        &__icon {
          :deep() {
            svg path {
              fill: $hoverIcon;
            }
          }
        }
      }
    }
  }

  .sharing-item {
    padding: $spacing-3 $spacing-6;
    transition: 0.2s all ease-in;
    cursor: pointer;
    display: flex;
    align-items: center;

    &--copy {
      @include sharingItemTheme(
        (
          'hoverBg': #e4fcfb,
          'hoverIcon': #000000,
        )
      );
    }

    &--finished {
      background: rgba(205, 34, 202, 0.1);

      .sharing-item__title {
        transition: 0.2s all ease-in;
        color: $default-color--1;
      }

      .sharing-item__icon {
        :deep() {
          svg path {
            fill: transparent;
          }
        }
      }
    }

    &--vkontakte {
      @include sharingItemTheme(
        (
          'hoverBg': #d9ebff,
          'hoverIcon': #0077ff,
        )
      );
    }

    &--twitter {
      @include sharingItemTheme(
        (
          'hoverBg': #c4c4c4,
          'hoverIcon': #000000,
        )
      );
    }

    &--telegram {
      @include sharingItemTheme(
        (
          'hoverBg': #d4ecf6,
          'hoverIcon': #2aa1d4,
        )
      );
    }

    &--pinterest {
      @include sharingItemTheme(
        (
          'hoverBg': #fad4da,
          'hoverIcon': #eb3323,
        )
      );
    }

    &--viber {
      @include sharingItemTheme(
        (
          'hoverBg': #e5ddf3,
          'hoverIcon': #7e57c2,
        )
      );
    }

    &__icon {
      width: 24px;
      height: 24px;
      transition: 0.2s all ease-in;
      margin-right: $spacing-2;

      :deep() {
        svg path {
          fill: $icon-color--1;
        }
      }
    }

    &__title {
      @include normalText;
      color: #000000;
      font-family: $text-font--1;
    }
  }
</style>
